import type { GetScimConfigQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetScimConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

/**
 * Shape returned by `trpc.frontend.scim.getConfig` — mirrors the Hasura
 * action wire shape so the GraphQL adapter is a no-op `__typename` add.
 *
 * Kept inline (rather than imported from `@risksmart-app/trpc`) because the
 * router infers its return type from Zod schemas defined inside the router
 * module; re-exporting them would invert the package dependency.
 */
type ScimConfigTrpcOutput = {
  domains: {
    domain: string;
    createdOn: string;
  }[];
  tokens: {
    keyId: string;
    orgKey: string;
    createdOn: string;
    expiresOn: string;
    status: 'active' | 'expired' | 'revoked';
  }[];
  legacyTokens: boolean;
};

type ScimConfigHookResult = {
  __typename: 'query_root';
  getScimConfig: {
    __typename: 'GetScimConfigOutput';
    domains: (ScimConfigTrpcOutput['domains'][number] & {
      __typename: 'ScimDomainOutput';
    })[];
    tokens: (ScimConfigTrpcOutput['tokens'][number] & {
      __typename: 'ScimTokenOutput';
      token: string;
    })[];
    legacyTokens: boolean;
  };
};

const mapScimConfigToGraphQL = (
  data: ScimConfigTrpcOutput
): ScimConfigHookResult => ({
  __typename: 'query_root',
  getScimConfig: {
    __typename: 'GetScimConfigOutput',
    domains: data.domains.map((d) => ({
      __typename: 'ScimDomainOutput',
      domain: d.domain,
      createdOn: d.createdOn,
    })),
    // The Hasura action shape exposes `token` on every row (always empty
    // string for non-issue reads). The tRPC procedure omits it because
    // upstream never returns it for GET-config; pad with '' so the shape
    // is byte-compatible with the legacy GraphQL consumers.
    tokens: data.tokens.map((t) => ({
      __typename: 'ScimTokenOutput',
      keyId: t.keyId,
      orgKey: t.orgKey,
      token: '',
      createdOn: t.createdOn,
      expiresOn: t.expiresOn,
      status: t.status,
    })),
    legacyTokens: data.legacyTokens,
  },
});

export const useGetScimConfig = createQueryHook<
  void,
  ScimConfigTrpcOutput,
  GetScimConfigQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.scim.getConfig.queryOptions(),
  mapTrpcDataToGraphQL: mapScimConfigToGraphQL,
  graphqlDocument: GetScimConfigDocument,
});
